import React from 'react';
import Router from 'next/router';
import baseConfig from '@deezer/next/services/config/baseConfig';
import {
	BellOutlinedIcon,
	InformationIcon,
	WarningIcon,
	TrashIcon,
} from '@tempo/icons';
import {SupportedLocales} from '@constants/locales';
import type {ConfigValue} from './types';
import {ConfigKeys} from './types';

const supportedLocaleOptions = Object.entries(SupportedLocales)
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	.filter(([_, v]) => isNaN(Number(v)))
	.map(([k, v]) => ({label: k, value: v}));

export enum GroupId {
	TOAST = 'toast_group',
	DIDOMI = 'didomi_group',
	STYLING = 'styling_group',
	API_HOSTS = 'api_hosts_group',
	DEBUG = 'debug_group',
}

export const CONFIG_OPTIONS: {
	[key in ConfigKeys]: ConfigValue;
} = {
	[ConfigKeys.LOCALE]: {
		type: 'select',
		label: 'Locale',
		defaultValue: 'en-us',
		options: supportedLocaleOptions,
		onSelect: (value: string) => {
			Router.replace(Router.asPath, undefined, {locale: value});
		},
	},
	[ConfigKeys.API_HOST]: {
		type: 'string',
		label: 'Starmania V2',
		defaultValue: baseConfig.API_HOST,
		groupId: GroupId.API_HOSTS,
	},
	[ConfigKeys.LEGACY_API_HOST]: {
		type: 'string',
		label: 'Starmania V1',
		defaultValue: baseConfig.LEGACY_API_HOST,
		groupId: GroupId.API_HOSTS,
	},
	[ConfigKeys.THEME_MODE]: {
		type: 'select',
		label: 'Theme mode',
		defaultValue: 'default',
		groupId: GroupId.STYLING,
		options: [
			{label: 'Default', value: 'default'},
			{label: 'Light', value: 'light'},
			{label: 'Dark', value: 'dark'},
		],
	},
	[ConfigKeys.RESET_CSS]: {
		type: 'boolean',
		label: 'Enable ResetCSS',
		defaultValue: 'true',
		groupId: GroupId.STYLING,
	},
	[ConfigKeys.TOAST_INFO]: {
		type: 'button',
		label: 'Info toast',
		onClick: ({toast}) => {
			toast({
				title: 'Info toast',
				description: 'This is an informative toast description',
			});
		},
		groupId: GroupId.TOAST,
		buttonProps: {
			leftIcon: <BellOutlinedIcon />,
		},
	},
	[ConfigKeys.TOAST_WARNING]: {
		type: 'button',
		label: 'Warning toast',
		onClick: ({toast}) => {
			toast({
				status: 'warning',
				title: 'Warning toast',
				description: 'This is a warning toast description',
			});
		},
		groupId: GroupId.TOAST,
		buttonProps: {
			leftIcon: <InformationIcon />,
		},
	},
	[ConfigKeys.TOAST_ERROR]: {
		type: 'button',
		label: 'Error toast',
		onClick: ({toast}) => {
			toast({
				status: 'error',
				title: 'Error toast',
				description: 'This is an error toast description',
			});
		},
		groupId: GroupId.TOAST,
		buttonProps: {
			leftIcon: <WarningIcon />,
		},
	},
	[ConfigKeys.FORCE_SHOW_DIDOMI_BANNER]: {
		type: 'button',
		label: 'Show Didomi Banner',
		onClick: () => {
			window.Didomi.notice.show();
		},
		groupId: GroupId.DIDOMI,
	},
	[ConfigKeys.RESET_DIDOMI]: {
		type: 'button',
		label: 'Reset Didomi',
		onClick: ({toast}) => {
			window.Didomi.reset();
			toast({
				status: 'info',
				title: 'Didomi user consent reset',
				description: 'The user consent has been reset',
			});
		},
		groupId: GroupId.DIDOMI,
		buttonProps: {
			leftIcon: <TrashIcon />,
		},
	},
	[ConfigKeys.DISABLE_TOAST]: {
		type: 'boolean',
		label: 'Disable all toasts',
		defaultValue: 'false',
		groupId: GroupId.DEBUG,
	},
	[ConfigKeys.DISABLE_THUMBNAILS]: {
		type: 'boolean',
		label: 'Disable thumbnails',
		defaultValue: 'false',
		groupId: GroupId.DEBUG,
	},
};

export const GROUP_LABELS: {
	[key: string]: string;
} = {
	[GroupId.DIDOMI]: 'Didomi - User Cookie Consent',
	[GroupId.TOAST]: 'Toasts',
	[GroupId.STYLING]: 'Styling',
	[GroupId.API_HOSTS]: 'API Hosts',
	[GroupId.DEBUG]: 'Debugging tools',
};
